import React, { useState } from 'react';
import { getSocket } from '@/lib/socket';
import { useRoomStore } from '@/stores/roomStore';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useToastStore } from '@/components/Toast';

interface ParticipantsProps {
  roomId: string;
}

export const Participants: React.FC<ParticipantsProps> = () => {
  const socket = getSocket();
  const { participants, myRole, room } = useRoomStore();
  const addToast = useToastStore((state) => state.addToast);
  const [showManageModal, setShowManageModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const canManage = myRole === 'owner';
  const myUserId = socket.id;

  const getRoleBadge = (role: 'owner' | 'dj' | 'listener') => {
    const styles = {
      owner: 'badge-owner',
      dj: 'badge-dj',
      listener: 'badge-listener'
    };

    const icons = {
      owner: '👑',
      dj: '🎧',
      listener: '👤'
    };

    return (
      <span className={`badge ${styles[role]}`}>
        {icons[role]} {role.charAt(0).toUpperCase() + role.slice(1)}
      </span>
    );
  };

  const handleGrantDJ = (userId: string) => {
    socket.emit('dj:grant', { userId, permanent: false }, (response: { success: boolean; error?: string }) => {
      if (response.success) {
        addToast('DJ role granted successfully', 'success');
        setShowManageModal(false);
      } else {
        addToast(response.error || 'Failed to grant DJ role', 'error');
      }
    });
  };

  const handleRevokeDJ = (userId: string) => {
    socket.emit('dj:revoke', { userId }, (response: { success: boolean; error?: string }) => {
      if (response.success) {
        addToast('DJ role revoked successfully', 'success');
        setShowManageModal(false);
      } else {
        addToast(response.error || 'Failed to revoke DJ role', 'error');
      }
    });
  };

  const openManageModal = (userId: string) => {
    setSelectedUserId(userId);
    setShowManageModal(true);
  };

  const selectedParticipant = participants.find((p) => p.userId === selectedUserId);

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-primary">
          Participants ({participants.length})
        </h2>
        {room?.isConnected && (
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-lime-400 rounded-full animate-pulse" />
            <span className="text-xs text-tertiary">Connected</span>
          </div>
        )}
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto">
        {participants.map((participant) => (
          <div
            key={participant.userId}
            className="flex items-center justify-between p-3 bg-muted rounded-lg hover:opacity-80 transition-colors"
          >
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-lime-400 to-lime-500 flex items-center justify-center text-navy-900 font-bold">
                {participant.userName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1">
                <p className="font-bold text-primary">{participant.userName}</p>
                <p className="text-xs text-tertiary">
                  Joined {new Date(participant.joinedAt).toLocaleTimeString()}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {participant.userId === myUserId && (
                <span className="badge badge-lime">
                  Me
                </span>
              )}
              {getRoleBadge(participant.role)}
              {canManage && participant.role !== 'owner' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openManageModal(participant.userId)}
                  className="ml-2"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                  </svg>
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Manage Participant Modal */}
      <Modal
        isOpen={showManageModal}
        onClose={() => setShowManageModal(false)}
        title="Manage Participant"
        size="sm"
      >
        {selectedParticipant && (
          <div className="space-y-4">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-lime-400 to-lime-500 flex items-center justify-center text-navy-900 font-bold text-2xl mb-3">
                {selectedParticipant.userName.charAt(0).toUpperCase()}
              </div>
              <h3 className="text-lg font-bold text-primary">{selectedParticipant.userName}</h3>
              <div className="mt-2">{getRoleBadge(selectedParticipant.role)}</div>
            </div>

            <div className="space-y-2">
              {selectedParticipant.role === 'listener' ? (
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={() => handleGrantDJ(selectedParticipant.userId)}
                >
                  Grant DJ Role
                </Button>
              ) : (
                <Button
                  variant="danger"
                  className="w-full"
                  onClick={() => handleRevokeDJ(selectedParticipant.userId)}
                >
                  Revoke DJ Role
                </Button>
              )}
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => setShowManageModal(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
